import { Flex, Input, InputGroup, InputRightElement } from '@chakra-ui/react'
import React, { useEffect } from 'react'
import { MangoButtonVariant } from '~/components/shared/componentStyles/MangoButton.style'
import { MangoButton } from '~/components/shared/MangoButton'
import { useKeyPresses } from '~/hooks/useKeyboard'
import {
  ContactDetails,
  ContactNumber,
} from '~/pages/Contacts/viewModels/Contacts.viewmodel'
import { getDiffSummaryLengthAtOrBeforeIndex } from '~/pages/WebPhone/utils/stringUtils'
import { Size } from '~/types/ThemeTypes'

import {
  NumberInputVariants,
  useNumberInputStyles,
} from './componentStyles/NumberInput.style'

interface NumberInputProps {
  phonenumber: string
  outboundCallerIdName?: string
  onPhonenumberChange: (newPhoneNumber: string) => void
  onStartCall: (
    callee:
      | string
      | {
          contactNumber: ContactNumber
          contact: ContactDetails
        },
    outboundCallerIdName?: string,
  ) => void
  inputRef: React.RefObject<HTMLInputElement>
  disabled?: boolean
  callDisabled?: boolean
}
export const PhoneNumberInput = ({
  phonenumber,
  outboundCallerIdName,
  onPhonenumberChange,
  onStartCall,
  inputRef,
  disabled,
  callDisabled,
}: NumberInputProps) => {
  const [cursorPosition, setCursorPostion] = React.useState(0)
  const [previousInputValue, setPreviousInputValue] = React.useState<string>('')

  const keypress = useKeyPresses()

  //when any key is pressed, focus the input box
  useEffect(() => {
    if (disabled) {
      return
    }

    inputRef.current?.focus()
  }, [keypress])

  // Set input box cursor position when phonenumber changes.
  // It must compensate for formatting updates to the phonenumber.
  // Depends on cursorPosition and previousInputValue
  useEffect(() => {
    const adjustment = getDiffSummaryLengthAtOrBeforeIndex(
      previousInputValue,
      phonenumber,
      cursorPosition,
    )

    if (inputRef.current) {
      inputRef.current.selectionStart = cursorPosition + adjustment
      inputRef.current.selectionEnd = cursorPosition + adjustment
    }
  }, [cursorPosition, previousInputValue])

  const updatePhonenumber = (newPhonenumber: string) => {
    setPreviousInputValue(newPhonenumber)
    onPhonenumberChange(newPhonenumber)
  }
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updatePhonenumber(e.target.value)
    setCursorPostion(e.target.selectionStart ?? 0)
  }

  const handleClear = () => {
    onPhonenumberChange('')
  }

  const variant = disabled
    ? NumberInputVariants.Disabled
    : phonenumber
      ? NumberInputVariants.Filled
      : NumberInputVariants.Ready
  const styles = useNumberInputStyles({
    variant,
  })
  return (
    <Flex sx={styles.container}>
      <InputGroup>
        <Input
          sx={styles.input}
          ref={inputRef}
          value={phonenumber}
          variant="unstyled"
          placeholder="Enter Number"
          disabled={disabled}
          onChange={handleChange}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !callDisabled) {
              onStartCall(phonenumber, outboundCallerIdName)
            }
          }}
        />
        <InputRightElement sx={styles.inputRightElement}>
          <MangoButton
            onClick={handleClear}
            variant={MangoButtonVariant.Default_100}
            size={Size.sm}
          >
            Clear
          </MangoButton>
        </InputRightElement>
      </InputGroup>
    </Flex>
  )
}
